import React, { useState, useEffect } from 'react';
import { X, Save, User, Calendar, Activity } from "lucide-react";
import { toast } from 'react-toastify';
import { PatientData } from '../../types/supabase'; // Importa PatientData do Supabase
import {
  formatPatientPhoneInput,
  normalizePatientEmail,
  normalizePatientPhone,
  validatePatientEmail,
  validatePatientPhone,
} from '../../utils/patientRegistration';

interface EditPatientModalProps {
  patient: PatientData; // Usa a PatientData do Supabase
  onClose: () => void;
  // onSave: (patientId: string, updatedData: Partial<PatientData>) => Promise<void>;
  // A função onSave precisará ser ajustada para usar os nomes de campos do Supabase
  onSave: (patientId: string, updatedData: Partial<PatientData>) => Promise<void>;
  onDeleteRequest: (patient: PatientData) => void;
}

const EditPatientModal: React.FC<EditPatientModalProps> = ({ patient, onClose, onSave, onDeleteRequest }) => {
  const formatCepValue = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  // Estado inicial baseado na PatientData do Supabase
  const [formData, setFormData] = useState({
    patientFullName: patient.patientFullName || "",
    patientDateOfBirth: patient.patientDateOfBirth?.split("T")[0] || "",
    patientGender: patient.patientGender || "",
    patientEmail: patient.patientEmail || "",
    patientPhone: patient.patientPhone || "",
    patientHeightCm: patient.patientHeightCm || 0,
    patientWeightKg: patient.patientWeightKg || 0,
    patientMedicalInfoConditions: patient.patientMedicalInfoConditions?.join(", ") || "",
    patientAddressStreet: patient.patientAddressStreet || "",
    patientAddressNumber: patient.patientAddressNumber || "",
    patientAddressComplement: patient.patientAddressComplement || "",
    patientAddressNeighborhood: patient.patientAddressNeighborhood || "",
    patientAddressCity: patient.patientAddressCity || "",
    patientAddressState: patient.patientAddressState || "",
    patientAddressZipCode: patient.patientAddressZipCode || "",
    patientEmergencyContactName: patient.patientEmergencyContactName || "",
    patientEmergencyContactPhone: patient.patientEmergencyContactPhone || "",
    patientEmergencyContactRelationship: patient.patientEmergencyContactRelationship || "",
    patientMedicalInfoBloodType: patient.patientMedicalInfoBloodType || "",
    patientMedicalInfoAllergies: patient.patientMedicalInfoAllergies?.join(", ") || "",
    patientMedicalInfoMedications: patient.patientMedicalInfoMedications?.join(", ") || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedIMC, setCalculatedIMC] = useState(0);

  const calculateIMC = (weight: number, height: number): number => {
    if (!weight || !height || height === 0) return 0;
    const heightInMeters = height / 100;
    return weight / (heightInMeters * heightInMeters);
  };

  const getIMCClassification = (imc: number): string => {
    if (imc < 18.5) return "Abaixo do peso";
    if (imc < 25) return "Peso normal";
    if (imc < 30) return "Sobrepeso";
    if (imc < 35) return "Obesidade grau I";
    if (imc < 40) return "Obesidade grau II";
    return "Obesidade grau III";
  };

  const shouldConfirmLowPhysicalData = (heightCm: number, weightKg: number): boolean => {
    const isHeightLow = heightCm < 50;
    const isWeightLow = weightKg < 20;

    if (!isHeightLow && !isWeightLow) {
      return true;
    }

    const confirmationMessage = [
      "Os dados físicos informados estão abaixo do padrão esperado.",
      "Altura menor que 50 cm ou peso menor que 20 kg.",
      "Confirma que os dados estão corretos?"
    ].join("\n");

    return window.confirm(confirmationMessage);
  };

  const handleCepBlur = async (cep: string) => {
    const cleanedCep = cep.replace(/\D/g, "");
    if (cleanedCep.length !== 8) {
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP não encontrado.");
        return;
      }

      setFormData(prev => ({
        ...prev,
        patientAddressStreet: data.logradouro,
        patientAddressNeighborhood: data.bairro,
        patientAddressCity: data.localidade,
        patientAddressState: data.uf,
      }));

    } catch (error) {
      toast.error("Erro ao buscar CEP. Verifique sua conexão.");
      console.error("Erro ao buscar CEP:", error);
    }
  };

  useEffect(() => {
    const newFormData = {
      patientFullName: patient.patientFullName || "",
      patientDateOfBirth: patient.patientDateOfBirth?.split("T")[0] || "",
      patientGender: patient.patientGender || "",
      patientEmail: patient.patientEmail || "",
      patientPhone: patient.patientPhone || "",
      patientHeightCm: patient.patientHeightCm || 0,
      patientWeightKg: patient.patientWeightKg || 0,
      patientMedicalInfoConditions: patient.patientMedicalInfoConditions?.join(", ") || "",
      patientAddressStreet: patient.patientAddressStreet || "",
      patientAddressNumber: patient.patientAddressNumber || "",
      patientAddressComplement: patient.patientAddressComplement || "",
      patientAddressNeighborhood: patient.patientAddressNeighborhood || "",
      patientAddressCity: patient.patientAddressCity || "",
      patientAddressState: patient.patientAddressState || "",
      patientAddressZipCode: patient.patientAddressZipCode || "",
      patientEmergencyContactName: patient.patientEmergencyContactName || "",
      patientEmergencyContactPhone: patient.patientEmergencyContactPhone || "",
      patientEmergencyContactRelationship: patient.patientEmergencyContactRelationship || "",
      patientMedicalInfoBloodType: patient.patientMedicalInfoBloodType || "",
      patientMedicalInfoAllergies: patient.patientMedicalInfoAllergies?.join(", ") || "",
      patientMedicalInfoMedications: patient.patientMedicalInfoMedications?.join(", ") || "",
    };
    setFormData(newFormData);
  }, [patient]);

  useEffect(() => {
    const imc = calculateIMC(formData.patientWeightKg, formData.patientHeightCm);
    setCalculatedIMC(imc);
  }, [formData.patientWeightKg, formData.patientHeightCm]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações usando os novos nomes de campos
    if (!formData.patientFullName.trim()) {
      toast.error('Nome completo é obrigatório');
      return;
    }

    if (!formData.patientDateOfBirth) {
      toast.error('Data de nascimento é obrigatória');
      return;
    }

    if (!formData.patientGender) {
      toast.error('Sexo é obrigatório');
      return;
    }

    const emailValidationError = validatePatientEmail(formData.patientEmail);
    if (emailValidationError) {
      toast.error(emailValidationError);
      return;
    }

    const phoneValidationError = validatePatientPhone(formData.patientPhone);
    if (phoneValidationError) {
      toast.error(phoneValidationError);
      return;
    }

    if (formData.patientHeightCm <= 0 || formData.patientWeightKg <= 0) {
      toast.error('Altura e peso devem ser maiores que zero');
      return;
    }

    if (formData.patientHeightCm > 250) {
      toast.error('Altura deve ser menor ou igual a 250 cm');
      return;
    }

    if (formData.patientWeightKg > 300) {
      toast.error('Peso deve ser menor ou igual a 300 kg');
      return;
    }

    if (!shouldConfirmLowPhysicalData(formData.patientHeightCm, formData.patientWeightKg)) {
      toast.info("Atualização cancelada para revisão de altura e peso.");
      return;
    }

    try {
      setIsSubmitting(true);

      const updateData: Partial<PatientData> = {
        patientFullName: formData.patientFullName.trim(),
        patientDateOfBirth: formData.patientDateOfBirth,
        patientGender: formData.patientGender,
        patientEmail: normalizePatientEmail(formData.patientEmail),
        patientPhone: normalizePatientPhone(formData.patientPhone),
        patientHeightCm: Number(formData.patientHeightCm),
        patientWeightKg: Number(formData.patientWeightKg),
        patientMedicalInfoConditions: formData.patientMedicalInfoConditions?.split(",").map(s => s.trim()).filter(s => s),
        patientAddressStreet: formData.patientAddressStreet,
        patientAddressNumber: formData.patientAddressNumber,
        patientAddressComplement: formData.patientAddressComplement,
        patientAddressNeighborhood: formData.patientAddressNeighborhood,
        patientAddressCity: formData.patientAddressCity,
        patientAddressState: formData.patientAddressState,
        patientAddressZipCode: formData.patientAddressZipCode,
        patientEmergencyContactName: formData.patientEmergencyContactName,
        patientEmergencyContactPhone: formData.patientEmergencyContactPhone,
        patientEmergencyContactRelationship: formData.patientEmergencyContactRelationship,
        patientMedicalInfoBloodType: formData.patientMedicalInfoBloodType,
        patientMedicalInfoAllergies: formData.patientMedicalInfoAllergies?.split(",").map(s => s.trim()).filter(s => s),
        patientMedicalInfoMedications: formData.patientMedicalInfoMedications?.split(",").map(s => s.trim()).filter(s => s),
        patientUpdatedAt: new Date().toISOString()
      };

      await onSave(patient.patientId, updateData); // Usar patient.patientId
      toast.success('Paciente atualizado com sucesso!');
      onClose();
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      toast.error('Erro ao salvar alterações');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <User className="w-6 h-6 text-primary-600 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Editar Paciente</h2>
                <p className="text-sm text-gray-600">Prontuário: {patient.patientId}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-blue-900">Dados Pessoais</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.patientFullName}
                    onChange={(e) => handleChange('patientFullName', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Nascimento *
                  </label>
                  <input
                    type="date"
                    value={formData.patientDateOfBirth}
                    onChange={(e) => handleChange('patientDateOfBirth', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sexo *
                  </label>
                  <select
                    value={formData.patientGender}
                    onChange={(e) => handleChange('patientGender', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Selecione</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Feminino">Feminino</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.patientEmail}
                    onChange={(e) => handleChange('patientEmail', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="paciente@exemplo.com"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Celular/WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={formData.patientPhone}
                    onChange={(e) => handleChange('patientPhone', formatPatientPhoneInput(e.target.value))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="(43) 99999-9999"
                    inputMode="tel"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-4">
                <Activity className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-green-900">Dados Físicos</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Altura (cm) *
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    max="250"
                    step="0.1"
                    value={formData.patientHeightCm || ''}
                    onChange={(e) => handleChange('patientHeightCm', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Ex: 170"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peso (kg) *
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    max="300"
                    step="0.1"
                    value={formData.patientWeightKg || ''}
                    onChange={(e) => handleChange('patientWeightKg', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Ex: 70.5"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {calculatedIMC > 0 && (
                <div className="mt-4 p-3 bg-white border border-green-300 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">IMC Calculado:</span>
                    <div className="text-right">
                      <span className="text-lg font-bold text-green-700">
                        {calculatedIMC.toFixed(1)}
                      </span>
                      <div className="text-sm text-gray-600">
                        {getIMCClassification(calculatedIMC)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-4">Endereço</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input
                    type="text"
                    value={formData.patientAddressZipCode}
                    onChange={(e) => handleChange("patientAddressZipCode", formatCepValue(e.target.value))}
                    onBlur={(e) => handleCepBlur(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                    placeholder="Digite o CEP"
                    inputMode="numeric"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rua</label>
                  <input type="text" value={formData.patientAddressStreet} onChange={(e) => handleChange("patientAddressStreet", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input type="number" value={formData.patientAddressNumber} onChange={(e) => handleChange("patientAddressNumber", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input type="text" value={formData.patientAddressComplement} onChange={(e) => handleChange("patientAddressComplement", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input type="text" value={formData.patientAddressNeighborhood} onChange={(e) => handleChange("patientAddressNeighborhood", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input type="text" value={formData.patientAddressCity} onChange={(e) => handleChange("patientAddressCity", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input type="text" value={formData.patientAddressState} onChange={(e) => handleChange("patientAddressState", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-4">Contato de Emergência</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input type="text" value={formData.patientEmergencyContactName} onChange={(e) => handleChange("patientEmergencyContactName", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    type="text"
                    value={formData.patientEmergencyContactPhone}
                    onChange={(e) => handleChange("patientEmergencyContactPhone", formatPatientPhoneInput(e.target.value))}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                    inputMode="tel"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parentesco</label>
                  <input type="text" value={formData.patientEmergencyContactRelationship} onChange={(e) => handleChange("patientEmergencyContactRelationship", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm" />
                </div>
              </div>
            </div>
            
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-cyan-900 mb-4">Informações Médicas Adicionais</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Sanguíneo</label>
                        <select value={formData.patientMedicalInfoBloodType} onChange={(e) => handleChange("patientMedicalInfoBloodType", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm">
                            <option value="">Selecione</option>
                            <option value="A+">A+</option>
                            <option value="A-">A-</option>
                            <option value="B+">B+</option>
                            <option value="B-">B-</option>
                            <option value="AB+">AB+</option>
                            <option value="AB-">AB-</option>
                            <option value="O+">O+</option>
                            <option value="O-">O-</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Alergias (separadas por vírgula)</label>
                        <textarea value={formData.patientMedicalInfoAllergies} onChange={(e) => handleChange("patientMedicalInfoAllergies", e.target.value)} rows={2} className="w-full rounded-md border-gray-300 shadow-sm" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Medicamentos em uso (separados por vírgula)</label>
                        <textarea value={formData.patientMedicalInfoMedications} onChange={(e) => handleChange("patientMedicalInfoMedications", e.target.value)} rows={2} className="w-full rounded-md border-gray-300 shadow-sm" />
                    </div>
                </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center mb-4">
                <Calendar className="w-5 h-5 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-orange-900">Informações Médicas</h3>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diagnósticos
                </label>
                <textarea
                  value={formData.patientMedicalInfoConditions}
                  onChange={(e) => handleChange("patientMedicalInfoConditions", e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Descreva os diagnósticos e condições médicas do paciente..."
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => onDeleteRequest(patient)}
                className="px-6 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Excluir paciente
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPatientModal;
